import React, { useState, useEffect } from 'react';
import ServiceDetail from './ServiceDetail';
import { api } from '../../src/services/api-new';

interface Props {
  services?: any[];
  vets?: any[];
  onBookAppointment?: () => void;
  onShop?: () => void;
  onNavigate?: (view: any) => void;
}

const LandingPage: React.FC<Props> = ({ services: externalServices, vets, onBookAppointment, onShop, onNavigate }) => {
  const [selectedService, setSelectedService] = useState<any>(null);
  const [servicesData, setServicesData] = useState<any[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (externalServices && externalServices.length > 0) {
        setServicesData(externalServices);
        setIsDataLoading(false);
        return;
      }

      setIsDataLoading(true);
      try {
        const data = await api.services.getAll();
        setServicesData(data.services || data);
      } catch (error) {
        console.error("Failed to fetch services:", error);
      } finally {
        setIsDataLoading(false);
      }
    };
    fetchData();
  }, [externalServices]);

  if (selectedService) {
    return <ServiceDetail service={selectedService} onBack={() => setSelectedService(null)} />;
  }

  if (isDataLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-surface-light dark:bg-background-dark">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
          <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">Preparing your experience...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-surface-light dark:bg-background-dark scroll-smooth">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-12 pb-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-50 via-white to-primary-50/30 dark:from-background-dark dark:via-background-dark dark:to-primary/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left Content */}
            <div className="text-center lg:text-left space-y-8 z-10 relative">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary-dark dark:bg-primary/20 dark:text-primary-light text-xs font-bold uppercase tracking-widest border border-primary/20 animate-fade-in">
                <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                Now Open: Premium Grooming Spa
              </div>

              <h1 className="text-5xl lg:text-8xl font-black tracking-tighter leading-[1] text-primary-dark dark:text-white animate-fade-in-up">
                Care They <span className="text-primary relative inline-block">
                  Deserve
                  <svg className="absolute w-full h-4 -bottom-2 left-0 text-primary-light opacity-30" viewBox="0 0 100 10" preserveAspectRatio="none">
                    <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" />
                  </svg>
                </span>,<br />
                Products They <span className="text-primary-dark dark:text-gray-300">Love</span>.
              </h1>

              <p className="text-xl text-gray-500 dark:text-gray-400 max-w-lg mx-auto lg:mx-0 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                Join thousands of happy pet parents who trust PetCareHub for expert veterinary care and premium lifestyle essentials.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                <button
                  onClick={() => onBookAppointment && onBookAppointment()}
                  className="group bg-primary hover:bg-primary-dark text-white text-lg font-bold px-8 py-5 rounded-3xl transition-all shadow-2xl shadow-primary/30 flex items-center justify-center gap-3 transform hover:-translate-y-1"
                >
                  Book Appointment
                  <span className="material-icons-round group-hover:rotate-12 transition-transform">calendar_today</span>
                </button>
                <button
                  onClick={() => onShop && onShop()}
                  className="bg-white dark:bg-surface-dark border-2 border-gray-100 dark:border-gray-800 hover:border-primary text-gray-900 dark:text-white text-lg font-bold px-8 py-5 rounded-3xl transition-all flex items-center justify-center gap-3 hover:shadow-xl active:scale-95"
                >
                  Shop Catalog
                  <span className="material-icons-round">shopping_bag</span>
                </button>
              </div>

              <div className="pt-8 flex flex-wrap items-center justify-center lg:justify-start gap-8 text-sm font-bold text-gray-400 dark:text-gray-500 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                <div className="flex items-center gap-2"><span className="material-icons-round text-primary text-xl">verified</span> Top-Rated Vets</div>
                <div className="flex items-center gap-2"><span className="material-icons-round text-primary text-xl">verified</span> Fast Shipping</div>
                <div className="flex items-center gap-2"><span className="material-icons-round text-primary text-xl">verified</span> 24/7 Support</div>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="absolute -inset-10 bg-primary/20 rounded-full blur-[100px] opacity-30 animate-pulse"></div>
              <div className="aspect-[4/3] rounded-[3rem] overflow-hidden relative shadow-[0_32px_64px_-16px_rgba(0,0,0,0.15)] dark:shadow-none border-8 border-white dark:border-surface-dark group">
                <img
                  src="https://images.unsplash.com/photo-1548199973-03cce0bbc87b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1469&q=80"
                  alt="Happy pets receiving professional care"
                  className="w-full h-full object-cover transform scale-105 group-hover:scale-110 transition-transform duration-1000"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/20 to-transparent"></div>

                {/* Floating Card */}
                <div className="absolute bottom-10 left-10 right-10 flex justify-between items-center bg-white/80 dark:bg-surface-dark/80 backdrop-blur-xl p-6 rounded-[2rem] shadow-2xl border border-white/20">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-primary flex items-center justify-center text-white shadow-lg">
                      <span className="material-icons-round text-2xl">star</span>
                    </div>
                    <div>
                      <p className="text-2xl font-black text-primary-dark dark:text-white">4.9/5.0</p>
                      <p className="text-xs text-gray-500 font-bold uppercase tracking-widest">Client Satisfaction</p>
                    </div>
                  </div>
                  <div className="h-10 w-px bg-gray-200 dark:bg-gray-700 mx-4"></div>
                  <div className="flex -space-x-3">
                    {[1, 2, 3, 4].map(i => (
                      <img key={i} src={`https://i.pravatar.cc/100?u=${i + 10}`} className="w-10 h-10 rounded-full border-2 border-white dark:border-surface-dark" alt="User avatar" />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-32 bg-white dark:bg-background-dark relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full pointer-events-none overflow-hidden opacity-50 dark:opacity-20">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary-light/20 blur-[100px] rounded-full"></div>
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-blue-100/30 blur-[120px] rounded-full"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20 max-w-3xl mx-auto space-y-4">
            <span className="text-primary font-black uppercase tracking-widest text-xs">Our Expertise</span>
            <h2 className="text-4xl md:text-6xl font-black text-primary-dark dark:text-white tracking-tight">Comprehensive Care Services</h2>
            <div className="w-20 h-1.5 bg-primary mx-auto rounded-full"></div>
            <p className="text-xl text-gray-500 dark:text-gray-400 pt-4">From preventive wellness to critical emergency interventions, we provide the highest standard of veterinary medicine.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {servicesData.map(service => (
              <ServiceCard
                key={service.id}
                id={service.id}
                icon={service.icon}
                title={service.label}
                desc={service.desc}
                color={service.color}
                bg={service.bg}
                onClick={() => setSelectedService(service)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-surface-light dark:bg-surface-dark/20 border-t border-gray-100 dark:border-gray-800 pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center sm:text-left">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-20">
            <div className="col-span-1 md:col-span-1">
              <div className="flex items-center gap-2 mb-6 justify-center sm:justify-start">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center text-white shadow-lg shadow-primary/30">
                  <span className="material-icons-round text-xl">pets</span>
                </div>
                <span className="font-black text-2xl tracking-tighter text-primary-dark dark:text-white">PetCareHub</span>
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed mb-6">
                Redefining pet health through specialized veterinary care and a community-first approach to pet parenthood.
              </p>
              <div className="flex gap-4 justify-center sm:justify-start">
                <SocialIcon icon="facebook" />
                <SocialIcon icon="instagram" />
                <SocialIcon icon="twitter" />
              </div>
            </div>

            <FooterColumn title="Quick Links" links={['About Us', 'Services', 'Our Vets', 'Contact']} />
            <FooterColumn title="Support" links={['Help Center', 'Booking Tips', 'Shipping Info', 'Privacy Policy']} />

            <div>
              <h4 className="font-black text-sm uppercase tracking-widest mb-6 text-primary-dark dark:text-white">Newsletter</h4>
              <p className="text-xs text-gray-400 mb-4">Get the latest pet care tips and store offers.</p>
              <div className="flex gap-2">
                <input className="flex-1 bg-white dark:bg-background-dark border border-gray-100 dark:border-gray-800 rounded-xl px-4 py-3 text-xs focus:ring-2 focus:ring-primary outline-none" placeholder="Enter email" />
                <button onClick={() => alert("Subscribed to newsletter!")} className="bg-primary text-white p-3 rounded-xl shadow-lg shadow-primary/20 hover:bg-primary-dark transition-colors"><span className="material-icons-round text-sm">send</span></button>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 dark:border-gray-800 pt-12 flex flex-col md:flex-row justify-between items-center gap-6 text-xs text-gray-400 font-bold uppercase tracking-widest">
            <p>&copy; 2023 Pet Care Hub. All Rights Reserved.</p>
            <div className="flex gap-8">
              <a href="#" className="hover:text-primary transition-colors">Terms</a>
              <a href="#" className="hover:text-primary transition-colors">Cookies</a>
              <a href="#" className="hover:text-primary transition-colors">API Docs</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

const ServiceCard = ({ icon, title, desc, color, bg, onClick }: any) => (
  <div
    onClick={onClick}
    className="bg-white dark:bg-surface-dark p-10 rounded-[2.5rem] shadow-[0_10px_40px_-10px_rgba(0,0,0,0.05)] hover:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] transition-all cursor-pointer group border border-gray-100 dark:border-gray-800 hover:border-primary/20 flex flex-col items-center text-center hover:-translate-y-2 h-full"
  >
    <div className={`w-20 h-20 rounded-3xl ${bg || 'bg-primary/5'} ${color || 'text-primary'} flex items-center justify-center mb-8 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-inner`}>
      <span className="material-icons-round text-4xl">{icon}</span>
    </div>
    <h3 className="text-2xl font-black mb-4 text-primary-dark dark:text-white leading-tight">{title}</h3>
    <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed mb-8 flex-1">{desc}</p>
    <div className="inline-flex items-center gap-2 text-sm font-black text-primary group-hover:gap-4 transition-all uppercase tracking-widest">
      Explore Details <span className="material-icons-round text-lg">arrow_forward</span>
    </div>
  </div>
);

const SocialIcon = ({ icon }: any) => (
  <button onClick={() => alert(`Redirecting to ${icon}...`)} className="w-10 h-10 rounded-full bg-white dark:bg-surface-dark border border-gray-100 dark:border-gray-800 flex items-center justify-center text-gray-400 hover:text-primary hover:border-primary transition-all shadow-sm">
    <i className={`fab fa-${icon}`}></i>
  </button>
)

const FooterColumn = ({ title, links }: any) => (
  <div className="hidden sm:block">
    <h4 className="font-black text-sm uppercase tracking-widest mb-6 text-primary-dark dark:text-white">{title}</h4>
    <ul className="space-y-4">
      {links.map((link: string) => (
        <li key={link}><button onClick={() => alert(`Navigating to ${link}...`)} className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary transition-colors">{link}</button></li>
      ))}
    </ul>
  </div>
)

export default LandingPage;
